import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Tabs,
  Tab,
  Alert,
  Paper,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { getFirmData } from "../../../redux/Apps/Lawyerapp/DashboardSlice/DashboardSlice";
import { backend_api } from "../../../static/server";
import { useDialog } from "../../DialogContext";
import { getAllBilles } from "../../../redux/Apps/Lawyerapp/BillingSlice/BillingSlice";
import { getCurrencySymbol } from "../../../pages/LawyerPages/Settings/CurrencySetForm";

const BillGenerateForm = ({ entryData }) => {
  const { closeDialog } = useDialog();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token } = useSelector((data) => data.user);
  const { firmData } = useSelector((state) => state.dashboard);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(
      getFirmData({
        url: `${backend_api}firm/create-law-firm/`,
        token: token,
      }),
    );
  }, [dispatch, token]);

  const onlyFirstFirm =
    Array.isArray(firmData) && firmData.length > 0 ? firmData[0] : null;

  // Group data by matter (case ID) with proper data extraction
  const matterGroups = useMemo(() => {
    const groups = {};

    if (!entryData) return groups;

    // Handle single row from handleQuickBill
    if (entryData.full_data) {
      const row = entryData;
      const caseId = row.full_data?.case?.id || row.case;

      if (!caseId) {
        console.warn("Row missing case ID:", row);
        return groups;
      }

      const showOnBill =
        !row.full_data?.non_billable || row.full_data?.bill_entry_show;

      if (showOnBill) {
        groups[caseId] = {
          matterName:
            row.matter_name ||
            row.full_data?.case?.case_name ||
            `Case ${caseId}`,
          data: [row],
          ids: !row.full_data?.bill_genrated ? [row.id] : [],
          client:
            row.full_data?.case?.case_related_person?.id ||
            row.full_data?.case?.case_related_person,
          billGenerated: row.full_data?.bill_genrated || false,
          allGenerated: row.full_data?.bill_genrated || false,
          // Extract currency from the first entry
          currency: row.full_data?.currency || row.currency || "USD",
        };
      }
    }
    // Handle array of rows from handleBulkBill
    else if (Array.isArray(entryData)) {
      entryData.forEach((item) => {
        const row = item;

        // Extract case ID
        let caseId;
        if (row.full_data?.case?.id) {
          caseId = row.full_data.case.id;
        } else if (row.case) {
          caseId = row.case;
        } else {
          console.warn("Row missing case ID:", row);
          return;
        }

        const showOnBill =
          !row.full_data?.non_billable || row.full_data?.bill_entry_show;
        if (!showOnBill) {
          return;
        }

        if (!groups[caseId]) {
          groups[caseId] = {
            matterName:
              row.matter_name ||
              row.full_data?.case?.case_name ||
              `Case ${caseId}`,
            data: [],
            ids: [],
            client:
              row.full_data?.case?.case_related_person?.id ||
              row.full_data?.case?.case_related_person,
            billGenerated: false,
            allGenerated: false,
            // Initialize currency from first entry
            currency: row.full_data?.currency || row.currency || null,
            currencies: new Set(),
          };
        }

        // Add the row to data
        groups[caseId].data.push(row);

        // Track currencies for this matter
        const entryCurrency = row.full_data?.currency || row.currency;
        if (entryCurrency) {
          groups[caseId].currencies.add(entryCurrency);
        }

        // Only add to ids if not already billed
        if (!row.full_data?.bill_genrated) {
          groups[caseId].ids.push(row.id);
        } else {
          groups[caseId].billGenerated = true;
        }
      });

      // Check currency consistency and finalize currency for each matter
      Object.keys(groups).forEach((caseId) => {
        const group = groups[caseId];
        const nonGeneratedEntries = group.data.filter(
          (row) => !row.full_data?.bill_genrated,
        );

        if (nonGeneratedEntries.length === 0 && group.data.length > 0) {
          group.allGenerated = true;
        }

        // Determine the currency for this matter
        if (group.currencies.size > 0) {
          // Check if all entries have the same currency
          if (group.currencies.size === 1) {
            group.currency = Array.from(group.currencies)[0];
          } else {
            // Multiple currencies found - show warning and use the most common one
            console.warn(
              `Multiple currencies found for matter ${caseId}:`,
              Array.from(group.currencies),
            );
            // Use the currency from the first entry as fallback
            const firstEntry = group.data[0];
            group.currency =
              firstEntry.full_data?.currency || firstEntry.currency || "USD";
            group.currencyWarning = `Multiple currencies detected. Using ${group.currency} for all entries.`;
          }
        } else {
          group.currency = "USD"; // Default fallback
        }
      });
    }

    return groups;
  }, [entryData]);

  const matterIds = useMemo(() => Object.keys(matterGroups), [matterGroups]);

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      issue_date: dayjs().format("YYYY-MM-DD"),
      due_date: dayjs().format("YYYY-MM-DD"),
      status: "draft",
      currency: "USD", // Default value
    },
  });

  const onSubmit = async (data) => {
    const currentMatterId = matterIds[activeTab];
    const currentMatter = matterGroups[currentMatterId];

    if (!currentMatter) {
      toast.error("No matter data found");
      return;
    }

    if (currentMatter.allGenerated || currentMatter.ids.length === 0) {
      toast.info(
        `Bills for "${currentMatter.matterName}" have already been generated`,
      );

      // Move to the next tab if available
      if (activeTab < matterIds.length - 1) {
        setActiveTab(activeTab + 1);
      } else {
        navigate("/dashboard/bills");
        closeDialog();
      }
      return;
    }

    // Ensure we have a client ID
    if (!currentMatter.client) {
      toast.error("Client information is missing");
      return;
    }

    // Use the matter's determined currency
    const billCurrency = currentMatter.currency || data.currency || "USD";

    const body = {
      ...data,
      firm: onlyFirstFirm?.id,
      case: parseInt(currentMatterId),
      client: parseInt(currentMatter.client),
      expenses: currentMatter.ids,
      currency: billCurrency, // Add currency to payload
    };

    setLoading(true);
    try {
      const response = await axios.post(`${backend_api}firm-case/bill/`, body, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      toast.success(
        `Bill drafted successfully for "${currentMatter.matterName}" in ${billCurrency}`,
      );

      // If this is the last matter, navigate away
      if (activeTab === matterIds.length - 1) {
        navigate("/dashboard/bills");
        closeDialog();
      } else {
        // Move to the next tab
        setActiveTab(activeTab + 1);
      }
    } catch (error) {
      console.error("Error creating bill:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.detail ||
          "Failed to create bill",
      );
    } finally {
      setLoading(false);
      dispatch(
        getAllBilles({ url: `${backend_api}firm-case/bill/`, token: token }),
      );
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const columns = [
    { field: "matter_name", headerName: "Matter", width: 150 },
    { field: "due_date", headerName: "Due Date", width: 120 },
    { field: "type", headerName: "Type", width: 100 },
    { field: "description", headerName: "Description", width: 200 },
    { field: "category_name", headerName: "Category", width: 120 },
    { field: "quantity", headerName: "Qty", width: 80 },
    { field: "rate", headerName: "Rate", width: 100 },
    { field: "billable_amount", headerName: "Billable", width: 120 },
    { field: "bill_status", headerName: "Status", width: 130 },
  ];

  // Get current matter data for the table
  const currentMatterData = useMemo(() => {
    const currentMatterId = matterIds[activeTab];
    if (!currentMatterId || !matterGroups[currentMatterId]) return [];

    const currency = matterGroups[currentMatterId]?.currency || "USD";

    return matterGroups[currentMatterId].data.map((item) => ({
      ...item,
      due_date: watch("due_date") || dayjs().format("YYYY-MM-DD"),
      bill_status: item.full_data?.bill_genrated
        ? "Bill Generated"
        : "Ready to Draft",
      bill_genrated: item.full_data?.bill_genrated || false,
      matter_name: item.matter_name || item.full_data?.case?.case_name || "N/A",
      type: item.type || "N/A",
      description: item.description || "N/A",
      category_name: item.category_name || "N/A",
      quantity: item.quantity || "N/A",
      rate: `${currency} ${item.rate || "0"}`,
      billable_amount: `${currency} ${item.billable_amount || "0"}`,
    }));
  }, [matterIds, activeTab, matterGroups, watch("due_date")]);

  // Calculate total billable amount for current matter (only for non-generated bills)
  const totalBillableAmount = useMemo(() => {
    if (!currentMatterData.length) return "0.00";

    return currentMatterData
      .filter((item) => !item.bill_genrated)
      .reduce((sum, item) => {
        const amountStr = String(item.billable_amount || "0")
          .replace(/[^0-9.-]+/g, "")
          .split(" ")[0]; // Remove currency symbol
        const amount = parseFloat(amountStr) || 0;
        return sum + amount;
      }, 0)
      .toFixed(2);
  }, [currentMatterData]);

  // Get current matter currency
  const currentMatterCurrency = useMemo(() => {
    const currentMatterId = matterIds[activeTab];
    return matterGroups[currentMatterId]?.currency || "USD";
  }, [matterIds, activeTab, matterGroups]);

  // Check if current matter has any billable entries
  const currentMatterHasBillableEntries = useMemo(() => {
    const currentMatterId = matterIds[activeTab];
    if (!currentMatterId || !matterGroups[currentMatterId]) return false;

    return matterGroups[currentMatterId].ids.length > 0;
  }, [matterIds, activeTab, matterGroups]);

  const handleSkipToNext = () => {
    if (activeTab < matterIds.length - 1) {
      setActiveTab(activeTab + 1);
    } else {
      navigate("/dashboard/bills");
      closeDialog();
    }
  };

  if (Object.keys(matterGroups).length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">
          No data available to generate bills
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
          All selected entries may be non-billable or already billed.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        {matterIds.length > 1 && (
          <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {matterIds.map((matterId, i) => (
                <Tab
                  key={matterId}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span>
                        {matterGroups[matterId]?.matterName ||
                          `Matter ${matterId}`}
                      </span>
                      {matterGroups[matterId]?.allGenerated && (
                        <Typography
                          variant="caption"
                          sx={{
                            bgcolor: "success.light",
                            color: "success.contrastText",
                            px: 0.5,
                            borderRadius: 1,
                          }}
                        >
                          Billed
                        </Typography>
                      )}
                    </Box>
                  }
                  id={`matter-tab-${i}`}
                />
              ))}
            </Tabs>
          </Box>
        )}

        <Box
          sx={{
            p: "1rem",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            flex: 1,
            overflow: "hidden",
          }}
        >
          <Box sx={{ overflowY: "auto", flex: 1 }}>
            {matterGroups[matterIds[activeTab]]?.allGenerated && (
              <Alert severity="info" sx={{ mb: 2 }}>
                All bills for matter "
                {matterGroups[matterIds[activeTab]]?.matterName}" have already
                been generated.
              </Alert>
            )}

            {matterGroups[matterIds[activeTab]]?.billGenerated &&
              !matterGroups[matterIds[activeTab]]?.allGenerated && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Some entries for matter "
                  {matterGroups[matterIds[activeTab]]?.matterName}" have already
                  been billed. Only non-billed entries will be included in this
                  draft.
                </Alert>
              )}

            {matterGroups[matterIds[activeTab]]?.currencyWarning && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {matterGroups[matterIds[activeTab]]?.currencyWarning}
              </Alert>
            )}

            <Alert severity="info" sx={{ mb: 2 }}>
              Non-billable entries will appear in the draft only when "Show
              Entry on Bill" is enabled. Entries hidden here will also be hidden
              from the final bill preview.
            </Alert>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Controller
                name="issue_date"
                control={control}
                rules={{ required: "Issue Date is required" }}
                defaultValue={dayjs().format("YYYY-MM-DD")}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    {...field}
                    defaultValue={dayjs()}
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue) => {
                      if (newValue && dayjs(newValue).isValid()) {
                        field.onChange(dayjs(newValue).format("YYYY-MM-DD"));
                      } else {
                        field.onChange(null);
                      }
                    }}
                    sx={{ width: "100%" }}
                    slotProps={{
                      textField: {
                        label: (
                          <span>
                            Issue Date<span style={{ color: "red" }}>*</span>
                          </span>
                        ),
                        size: "small",
                        variant: "outlined",
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                    format="MM/DD/YYYY"
                  />
                )}
              />

              <Controller
                name="due_date"
                control={control}
                rules={{ required: "Due Date is required" }}
                defaultValue={dayjs().format("YYYY-MM-DD")}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    {...field}
                    defaultValue={dayjs()}
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue) => {
                      if (newValue && dayjs(newValue).isValid()) {
                        field.onChange(dayjs(newValue).format("YYYY-MM-DD"));
                      } else {
                        field.onChange(null);
                      }
                    }}
                    sx={{ width: "100%" }}
                    minDate={dayjs()}
                    slotProps={{
                      textField: {
                        label: (
                          <span>
                            Due Date<span style={{ color: "red" }}>*</span>
                          </span>
                        ),
                        size: "small",
                        variant: "outlined",
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                    format="MM/DD/YYYY"
                  />
                )}
              />
            </Box>

            {matterIds.length > 1 && (
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                Drafting Bill for Matter:{" "}
                {matterGroups[matterIds[activeTab]]?.matterName}
                (Bill {activeTab + 1} of {matterIds.length})
              </Typography>
            )}

            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
              Summary of the Bill
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 1, color: "text.secondary" }}
              >
                (Currency: {currentMatterCurrency})
              </Typography>
            </Typography>

            <Paper sx={{ width: "100%", overflow: "hidden" }}>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {columns.map((col) => (
                        <TableCell
                          key={col.field}
                          sx={{
                            fontWeight: 550,
                            fontSize: "0.8rem",
                            bgcolor: "background.paper",
                            whiteSpace: "nowrap",
                            minWidth: col.width || "auto",
                            position: "sticky",
                            top: 0,
                            zIndex: 10,
                          }}
                        >
                          {col.headerName}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentMatterData.length > 0 ? (
                      currentMatterData.map((row, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            backgroundColor: row.bill_genrated
                              ? "rgba(0, 0, 0, 0.04)"
                              : "inherit",
                            "&:hover": {
                              backgroundColor: "action.hover",
                            },
                          }}
                        >
                          {columns.map((col) => (
                            <TableCell
                              key={col.field}
                              sx={{
                                fontSize: "0.7rem",
                                color: row.bill_genrated
                                  ? "rgba(0, 0, 0, 0.6)"
                                  : "inherit",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: col.width || 150,
                              }}
                            >
                              <Tooltip
                                title={String(row[col.field] ?? "-")}
                                arrow
                                placement="top"
                              >
                                <Box
                                  sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row[col.field] ?? "-"}
                                </Box>
                              </Tooltip>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={columns.length} align="center">
                          No Data Available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Total Billable Amount:{" "}
                {getCurrencySymbol(currentMatterCurrency)}
                {totalBillableAmount}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            borderTop: "1px solid #ddd",
            position: "sticky",
            bottom: 0,
            backdropFilter: "blur(10px)",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: "0.5rem",
            zIndex: 1000,
            gap: "0.5rem",
            bgcolor: "background.paper",
            flexShrink: 0,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={() => closeDialog()}
            disabled={loading}
          >
            Cancel
          </Button>

          {/* Skip button for matters with all generated bills */}
          {!currentMatterHasBillableEntries &&
            activeTab < matterIds.length - 1 && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleSkipToNext}
                disabled={loading}
              >
                Skip to Next Matter
              </Button>
            )}

          {/* Regular submit button - disabled if all bills are generated */}
          <Button
            variant="contained"
            color="primary"
            type="submit"
            size="small"
            disabled={!currentMatterHasBillableEntries || loading}
          >
            {loading
              ? "Processing..."
              : matterIds.length > 1 && activeTab < matterIds.length - 1
                ? `Draft Bill & Continue to Next Matter`
                : `Draft Bill`}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default BillGenerateForm;
